import {
  CurrencyAmount,
  NonfungiblePositionManager,
  unwrappedToken
} from '@cryptoalgebra/kim-sdk';
import { useAddress, useChainId } from '@thirdweb-dev/react';
import { PositionFromTokenId } from '@app/hooks/v3/usePositions';
import { useContract } from '@thirdweb-dev/react-core';
import { algebraPositionManagerABI } from '../../../abis/v3';
import { Address } from '@thirdweb-dev/sdk';
import { MAX_UINT128 } from '@app/constants/common';
import { useAsync, useAsyncFn } from 'react-use';
import { getCurrencyFromKimToken } from '@app/helpers/currency';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@app/constants/queryKeys';
import { useConfig } from '@app/config';
import { getTokensWhitelist } from '@app/constants/tokensWhitelist';

export function useCollectAllFees(
  positions: PositionFromTokenId[] | undefined
) {
  const address = useAddress();
  const queryClient = useQueryClient();
  const config = useConfig();
  const chainId = useChainId();

  const { contract } = useContract(
    config?.ALGEBRA_ADDRESSES.ALGEBRA_POSITION_MANAGER,
    algebraPositionManagerABI
  );

  const { value } = useAsync(async () => {
    if (!address || !contract || !positions || !chainId) {
      return;
    }

    const callDataParams = await Promise.allSettled(
      positions.map(async pos => {
        const { tokenId, token0: token0Address, token1: token1Address } = pos;

        const token0 = getTokensWhitelist(config).find(
          t => t.contractAddress.toLowerCase() === token0Address.toLowerCase()
        );
        const token1 = getTokensWhitelist(config).find(
          t => t.contractAddress.toLowerCase() === token1Address.toLowerCase()
        );

        if (!token0 || !token1) {
          throw new Error('NO_TOKENS_FOUND');
        }

        const owner = await contract.call('ownerOf', [BigInt(tokenId)]);

        const amountsConfigTx = contract.prepare('collect', [
          {
            tokenId: BigInt(tokenId || 0),
            recipient: owner as Address,
            amount0Max: MAX_UINT128,
            amount1Max: MAX_UINT128
          }
        ]);

        const amounts = await amountsConfigTx.simulate();

        const currency0 = getCurrencyFromKimToken(token0, chainId);
        const currency1 = getCurrencyFromKimToken(token1, chainId);

        if (!currency0 || !currency1) {
          throw new Error('NO_TOKENS_FOUND');
        }

        const currencyAmounts = {
          amount0: CurrencyAmount.fromRawAmount(
            unwrappedToken(currency0),
            amounts[0].toString()
          ),
          amount1: CurrencyAmount.fromRawAmount(
            unwrappedToken(currency1),
            amounts[1].toString()
          )
        };

        if (
          currencyAmounts.amount0.toSignificant() === '0' &&
          currencyAmounts.amount1.toSignificant() === '0'
        ) {
          throw new Error('NO_FEES_AVAILABLE');
        }

        return NonfungiblePositionManager.collectCallParameters({
          tokenId,
          expectedCurrencyOwed0: currencyAmounts.amount0,
          expectedCurrencyOwed1: currencyAmounts.amount1,
          recipient: address
        });
      })
    );

    return callDataParams
      .filter(item => item.status === 'fulfilled')
      .map(
        item =>
          (
            item as unknown as PromiseFulfilledResult<{
              calldata: string[];
              value: string;
            }>
          ).value
      );
  }, [address, contract, positions, chainId]);

  const [{ loading: isLoading }, collectAll] = useAsyncFn(async () => {
    if (!contract || !value || !value.length) {
      return;
    }

    const callaDatas = value.map(v => v.calldata).flat();

    try {
      const res = await contract.call('multicall', [callaDatas], {
        value: BigInt(0)
      });

      if (res?.reason) {
        showErrorToast('Transaction rejected');
      }

      const txHash = res?.receipt?.transactionHash;

      if (txHash) {
        await queryClient.clear();
        await queryClient.invalidateQueries([QueryKeys.SINGLE_POOL]);
        await queryClient.invalidateQueries([QueryKeys.POOL_FEE_DATA]);
        await queryClient.invalidateQueries([QueryKeys.POSITION_FEES]);
        showSuccessToast(
          `Transaction successful.`,
          <ExternalLink txHash={txHash} />
        );
      }
    } catch (e: any) {
      console.error(e.message);

      if (e.message?.includes('user rejected transaction')) {
        return;
      }

      showErrorToast(`Transaction failed: ${e.message}`);
    }
  }, [contract, value]);

  return {
    isLoading,
    collectAll,
    isAvailable: value !== undefined && value.length > 0
  };
}
